/**
 * vd-guide-modal — lecteur plein ecran d'un guide video.
 *
 * - Sous-titres natifs (piste VTT de la locale, activee par defaut).
 * - Chapitres cliquables (timeline.json du pipeline) + surlignage au fil de
 *   la lecture.
 * - Vitesse 0.75 / 1 / 1.25 / 1.5 (rejoint le fast/slow du pipeline).
 * - Selecteur de langue : bascule la VARIANTE (video + vtt + poster) en
 *   conservant la position de lecture.
 * - ``closed`` emet true si l'utilisateur a regarde >= 80 % (marque "vu").
 * - Esc / clic backdrop pour fermer. RTL automatique en arabe.
 */

import { CommonModule } from '@angular/common';
import {
  Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output,
  ViewChild, inject, signal,
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

import { GuideEntry, GuideLocaleVariant, GuideService } from '../../services/guide.service';

@Component({
  selector: 'vd-guide-modal',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './guide-modal.component.html',
  styleUrls: ['./guide-modal.component.css'],
})
export class GuideModalComponent implements OnInit {
  @Input({ required: true }) guide!: GuideEntry;
  @Output() closed = new EventEmitter<boolean>();

  private readonly guideSvc = inject(GuideService);

  readonly locale = signal('fr');
  readonly variant = signal<GuideLocaleVariant | null>(null);
  readonly currentTime = signal(0);
  readonly rate = signal(1);
  readonly rates = [0.75, 1, 1.25, 1.5];
  private maxWatched = 0;

  @ViewChild('player') player?: ElementRef<HTMLVideoElement>;

  ngOnInit(): void {
    this.locale.set(this.guideSvc.locale());
    this.applyLocale(this.locale(), 0);
  }

  get availableLocales(): string[] {
    return Object.keys(this.guide?.locales || {});
  }

  localeLabel(l: string): string {
    return l === 'ar-TN' ? 'العربية' : l === 'en' ? 'English' : 'Français';
  }

  isRtl(): boolean {
    return this.locale() === 'ar-TN';
  }

  applyLocale(loc: string, resumeAt: number): void {
    const v = this.guide.locales[loc] || this.guide.locales['fr'];
    if (!v) return;
    this.locale.set(loc);
    this.variant.set(v);
    // La source change : on restaure la position apres le chargement.
    setTimeout(() => {
      const el = this.player?.nativeElement;
      if (!el) return;
      el.load();
      el.currentTime = resumeAt;
      el.playbackRate = this.rate();
      // Active la piste de sous-titres par defaut.
      el.textTracks && Array.from(el.textTracks).forEach((t) => (t.mode = 'showing'));
      el.play().catch(() => void 0);
    });
  }

  switchLocale(loc: string): void {
    if (loc === this.locale()) return;
    const t = this.player?.nativeElement?.currentTime || 0;
    this.applyLocale(loc, t);
  }

  setRate(r: number): void {
    this.rate.set(r);
    const el = this.player?.nativeElement;
    if (el) el.playbackRate = r;
  }

  seek(start: number): void {
    const el = this.player?.nativeElement;
    if (!el) return;
    el.currentTime = start + 0.05;
    el.play().catch(() => void 0);
  }

  onTimeUpdate(): void {
    const el = this.player?.nativeElement;
    if (!el) return;
    this.currentTime.set(el.currentTime);
    this.maxWatched = Math.max(this.maxWatched, el.currentTime);
  }

  isActiveChapter(start: number, duration: number): boolean {
    const t = this.currentTime();
    return t >= start && t < start + duration;
  }

  fmt(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  close(): void {
    const total = this.variant()?.duration_sec || 0;
    const watched = total > 0 && this.maxWatched >= 0.8 * total;
    this.player?.nativeElement?.pause();
    this.closed.emit(watched);
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    this.close();
  }
}
